import { getRequestEvent } from "solid-js/web";
import { RIASRecordType, padId } from "~/lib";
import { runBatchGetCommand } from "./dynamoDB";

export const getCognitoProfile = async () => {
	"use server";

	const { getUserFromEvent } = await import("./auth.server");

	const user = await getUserFromEvent();

	return user;
};

export const getUserCertificatesServer = async (limit: number = 25) => {
	"use server";
	const { getUserFromEvent } = await import("./auth.server");
	const { getByUser, batchGet } = await import("./dynamoDB");

	const user = await getUserFromEvent(getRequestEvent());

	if (!user) return new Response("Unauthorized", { status: 401 });

	const c = await getByUser("CERTIFICATE", user.userId!, limit);

	const projectIds = Array.from(
		new Map(
			c?.items?.map(item => [
				item.parent,
				{ pk: `USER#${user.userId!}`, sk: item.parent },
			]),
		).values(),
	);
	const projects = await batchGet(projectIds);

	return { projects, certificates: c.items };
};

export const getUserSubscriptionsServer = async () => {
	"use server";

	const { getByUser } = await import("./dynamoDB");

	return await getByUser("SUBSCRIPTION");
};

export const getProjectCertificatesServer = async (projectId: number) => {
	"use server";

	const { runQueryCommand, RIASTABLE } = await import("./dynamoDB");

	const r = await runQueryCommand({
		TableName: RIASTABLE,
		IndexName: "gsiByParent",
		KeyConditionExpression: "parent = :parent AND begins_with(sk, :prefix)",
		ExpressionAttributeValues: {
			":parent": padId(projectId, RIASRecordType.PROJECT),
			":prefix": "CERTIFICATE#",
		},
		ScanIndexForward: false,
	});

	return r?.items ?? [];
};

export const getByUserQueryServer = async (
	rowType: string,
	limit:number = 25,
	skId:string | null = null,
	userId:number|undefined = undefined,
) => {
	"use server";

	const { getByUser } = await import("./dynamoDB");

	return await getByUser(rowType, userId, limit, skId);
};

export const getByRowTypeServer = async (
	rowType: string,
	Limit: number = 25,
	ProjectionExpression: string | undefined = undefined,
	expressionAttributesNames = {},
	ExclusiveStartKey = undefined,
) => {
	"use server";

	//console.log( "getByRowTypeServer", { rowType, Limit, ProjectionExpression, expressionAttributesNames, ExclusiveStartKey } );
	
	const { runQueryCommand, RIASTABLE } = await import("./dynamoDB");

	return await runQueryCommand({
		TableName: RIASTABLE,
		IndexName: "gsiByType",
		KeyConditionExpression: "recordType = :type",
		ExpressionAttributeValues: { ":type": rowType },
		ExpressionAttributeNames: expressionAttributesNames,
		ScanIndexForward: false,
		Limit,
		ProjectionExpression,
		ExclusiveStartKey,
	});
};

export const getProfileQueryServer = async (profileId?: number) => {
	"use server";

	const { getProfile } = await import("./dynamoDB");

	if (!profileId || profileId <= 0 || profileId == {}) {
		return null;
	}
	if (profileId > 0) {
		try {
			const r = await getProfile(profileId);
			return r;
		} catch (e) {
			console.log("getProfileQuery err", e);
		}
	}

	return null;
};

export const getCertifiersQueryServer = async (
	schemes: string[] = ["section-6-domestic", "section-6-non-domestic"],
	statuses: string[] = ["active"],
	withSubs: boolean = true,
) => {
	"use server";

	const { runBatchGetCommand } = await import("./dynamoDB");

	// 1. Get subscriptions that match the schemes and statuses
	const subs = await Promise.all(
		statuses.map(status =>
			runRecordByStatusQueryServer("SUBSCRIPTION", status).then(r =>
				r.items.filter(s => schemes.includes(s.scheme))
			)
		)
	).then(results => results.flat());

	// 3. Build unique ApprovedBody keys
	const approvedBodyKeys = [...new Set(subs.map(s => s.approvedBodyId))].map(
		id => ({
			pk: `APPROVEDBODY#${id}`,
			sk: `APPROVEDBODY#${String(id).padStart(7, "0")}`,
		}),
	);

	// 4. Batch get ApprovedBodies
	const approvedBodies = await runBatchGetCommand(approvedBodyKeys).then(r =>
		r?.reduce((acc, item) => {
			acc[item.pk] = item;
			return acc;
		}, {}),
	);

	const subsByUserPk = subs.reduce((acc, sub) => {
		if (!acc[sub.pk]) acc[sub.pk] = [];
		acc[sub.pk].push({
			...sub,
			approvedBody:
				approvedBodies?.[`APPROVEDBODY#${sub.approvedBodyId}`] || null,
		});
		return acc;
	}, {} );
	
	// 5. Build unique User keys
	const userKeys = [...new Set(subs.map(s => s.pk))].map(
		pk => ({
			pk,
			sk: padId(Number(pk.split('#')[1]), RIASRecordType.USER),
		}),
	);
	
	// 6. Batch get Users
	const users = await runBatchGetCommand( userKeys );
	
	users?.map( user => {
		user.subs = subsByUserPk[user.pk] || [];
		return user;
	} );
	

	return users;
};

export const getByPKQueryServer = async (pk: string, sk?: string) => {
	"use server";

	const { queryByPK } = await import("./dynamoDB");
	
	const r = await queryByPK(pk, sk);

	return r;
};

export const runRecordByStatusQueryServer = async (
	recordType: string,
	status: string,
	descending: boolean = false,
): Promise<{ items: any[]; lastKey: string | null }> => {
	"use server";

	const { runQueryCommand, RIASTABLE } = await import("./dynamoDB");

	const queryResult = await runQueryCommand({
		TableName: RIASTABLE,
		IndexName: "gsiByRecordStatus",

		KeyConditionExpression: "#status = :status AND #type = :type",
		ExpressionAttributeNames: {
			"#status": "status",
			"#type": "recordType",
		},
		ExpressionAttributeValues: {
			":status": status,
			":type": recordType,
		},
		ScanIndexForward: descending,
		ProjectionExpression: "pk, sk", // important
	});

	const keys = queryResult?.items;

	let items = [];

	if (keys && keys.length > 0) {
		const batchResult = await runBatchGetCommand(keys);
		items = batchResult;
	}

	return { items, lastKey: queryResult?.lastEvaluatedKey };
};
